import { useState } from "react";
import { useTranslation } from "react-i18next";
import DateTime from "utils/datetime";
import { ANY } from "helpers/constants";

const MONTHS = "january,february,march,april,may,june,july,august,september,october,november,december".split(
	","
);

const LATEST = "latest";
const TOTAL = "total";

function leadinZero(num) {
	if (String(num).length < 2) return `0${num}`;
	return String(num);
}

const convertToString = index => MONTHS[index];

export const compute = {
	urlToApiParam: (url = LATEST) => {
		const strInitial = String(url);
		if (strInitial) {
			if (strInitial === TOTAL) {
				return {
					day: null,
					month: null,
					year: null,
				};
			}
			if (strInitial === LATEST) {
				return {
					day: null,
					month: 0,
					year: null,
				};
			}
			const date = strInitial.split("-");
			if (date.length === 3) {
				return {
					day: parseInt(date[0], 10),
					month: parseInt(date[1], 10),
					year: parseInt(date[2], 10),
				};
			}
			if (date.length === 2) {
				return {
					day: null,
					month: parseInt(date[0], 10),
					year: parseInt(date[1], 10),
				};
			}
			if (date.length === 1 && !Number.isNaN(Number(date[0]))) {
				return {
					day: null,
					month: null,
					year: parseInt(date[0], 10),
				};
			}
		}
		return {
			day: null,
			month: 0,
			year: null,
		};
	},
};

const getInitialPeriod = (initial = LATEST) => {
	if (initial === TOTAL) {
		return null;
	}
	if (initial === LATEST) {
		return LATEST;
	}
	const date = initial.split("-");

	if (date.length === 3) {
		return initial;
	}
	if (date.length === 2) {
		return Number(date[0]);
	}
	if (date.length === 1 && !Number.isNaN(Number(date[0]))) {
		return TOTAL;
	}
	return LATEST;
};

const getInitialYear = (initial = LATEST) => {
	if (initial === TOTAL) {
		return TOTAL;
	}
	if (initial === LATEST) {
		return new Date().getFullYear();
	}
	const date = initial.split("-");
	if (date.length === 3) {
		return Number(date[2]);
	}
	if (date.length === 2) {
		return Number(date[1]);
	}
	if (date.length === 1 && !Number.isNaN(Number(date[0]))) {
		return Number(date[0]);
	}
	return new Date().getFullYear();
};

const useFilterPariodExtended = ({ initial, fromYear = 2014 }) => {
	const currentYear = new Date().getFullYear();
	const { t } = useTranslation(["common", "filtersAndSelectors"]);

	const [selected, setSelected] = useState({
		period: getInitialPeriod(initial),
		year: getInitialYear(initial),
	});

	const months = Array.from(
		Array(
			selected.year === currentYear
				? new DateTime().getDate().getMonth() + 1
				: 12
		).keys()
	)
		.map(monthIndex => ({
			key: `month-${monthIndex + 1}`,
			value: monthIndex + 1,
			content: t(`common:months.${convertToString(monthIndex)}`),
			meta: {
				url: leadinZero(monthIndex + 1),
				month: true,
			},
		}))
		.reverse();
	const dt = new DateTime();

	const currentYearData = [
		{
			key: LATEST,
			value: LATEST,
			content: t("filtersAndSelectors:period.latest"),
			meta: {
				url: LATEST,
				latest: true,
			},
		},
		{
			key: "today",
			value: dt.format("DD-MM-YYYY"),
			content: dt.format("DD.MM.YYYY"),
			meta: {
				url: dt.format("DD-MM-YYYY"),
				day: true,
			},
		},
		{
			key: "yesterday",
			value: dt.modify(`-1 day`).format("DD-MM-YYYY"),
			content: dt.format("DD.MM.YYYY"),
			meta: {
				url: dt.format("DD-MM-YYYY"),
				day: true,
			},
		},
		{
			key: "1DayAgo",
			value: dt.modify(`-1 day`).format("DD-MM-YYYY"),
			content: dt.format("DD.MM.YYYY"),
			meta: {
				url: dt.format("DD-MM-YYYY"),
				day: true,
			},
		},
		{
			key: "2DayAgo",
			value: dt.modify(`-1 day`).format("DD-MM-YYYY"),
			content: dt.format("DD.MM.YYYY"),
			meta: {
				url: dt.format("DD-MM-YYYY"),
				day: true,
			},
		},
		{
			key: "3DayAgo",
			value: dt.modify(`-1 day`).format("DD-MM-YYYY"),
			content: dt.format("DD.MM.YYYY"),
			meta: {
				url: dt.format("DD-MM-YYYY"),
				day: true,
			},
		},
		{
			key: "4DayAgo",
			value: dt.modify(`-1 day`).format("DD-MM-YYYY"),
			content: dt.format("DD.MM.YYYY"),
			meta: {
				url: dt.format("DD-MM-YYYY"),
				day: true,
			},
		},
		{
			key: "5DayAgo",
			value: dt.modify(`-1 day`).format("DD-MM-YYYY"),
			content: dt.format("DD.MM.YYYY"),
			meta: {
				url: dt.format("DD-MM-YYYY"),
				day: true,
			},
		},
	];

	const PERIOD_ITEMS =
		selected.year === currentYear
			? [
					...(selected.year === currentYear ? currentYearData : []),
					...months,
					{
						key: TOTAL,
						value: TOTAL,
						content: t("filtersAndSelectors:period.total"),
						meta: {
							url: TOTAL,
						},
					},
			  ]
			: [
					...(selected.year === currentYear ? currentYearData : []),
					...months,
					{
						key: TOTAL,
						value: TOTAL,
						content: t("filtersAndSelectors:period.total"),
						meta: {
							url: TOTAL,
						},
					},
			  ].reverse();

	const year = new Date().getFullYear();
	const startYear = year - (year - fromYear);

	const years = year - startYear;

	let YEAR_ITEMS = [];

	for (let i = 0; i <= years; i++) {
		YEAR_ITEMS[i] = {
			key: i,
			content: startYear + i,
			value: startYear + i,
			meta: { url: startYear + i, year: true },
		};
	}

	YEAR_ITEMS = [
		...YEAR_ITEMS.reverse(),
		{
			key: TOTAL,
			content: `${t(
				"filtersAndSelectors:period.total"
			)} ${fromYear}-${new Date().getFullYear()}`,
			value: TOTAL,
			meta: { url: TOTAL },
		},
	];
	const computeHook = {
		periodValueToUrl: item => {
			const period = PERIOD_ITEMS.find(({ value }) => item === value);
			if (period.meta.month) {
				const year = YEAR_ITEMS.find(
					({ value }) => value === selected.year
				);
				return `${period.meta.url}-${year.meta.url}`;
			}
			if (period.value === TOTAL) {
				const year = YEAR_ITEMS.find(
					({ value }) => value === selected.year
				);
				return year?.meta.url ?? ANY;
			}

			return period?.meta.url ?? ANY;
		},
		yearValueUrl: data => {
			const period = PERIOD_ITEMS.find(
				({ value }) => value === selected.period
			);

			const year = YEAR_ITEMS.find(({ value }) => value === data);

			if (year.value === TOTAL) {
				return TOTAL;
			}
			if (year.value === currentYear) {
				return LATEST;
			}
			if (!period) {
				return year.meta.url;
			}
			if (year.meta.year && period.meta.month) {
				return `${period.meta.url}-${year.meta.url}`;
			}
			if (year.meta.year) {
				return year.meta.url;
			}

			return ANY;
		},
		yearValueToState: (year, prevState) => {
			const { period } = prevState;

			if (year === TOTAL) {
				return {
					period: null,
					year,
				};
			}

			if (year === currentYear) {
				return {
					period: LATEST,
					year,
				};
			}

			if (
				prevState.year === currentYear ||
				(!prevState.period && year !== currentYear)
			) {
				return {
					period: TOTAL,
					year,
				};
			}
			return {
				period,
				year,
			};
		},
		periodValueToState: (period, prevState) => {
			const { year } = prevState;
			return {
				period,
				year,
			};
		},
	};

	return {
		selected,
		setSelected,
		periodItems: PERIOD_ITEMS,
		yearItems: YEAR_ITEMS,
		compute: {
			...compute,
			...computeHook,
		},
	};
};

export default useFilterPariodExtended;
